import { useCallback, useRef } from "react";

/**
 * Collapses overlapping calls of an async action into a single run.
 *
 * PROA-2622: React state (`isExporting`, `isGenerating`, …) only gates a
 * button after a re-render, so a fast double click still lands twice. The ref
 * here flips synchronously, before React can render.
 *
 * Callers that arrive mid-flight get the *in-flight promise*, not `undefined`:
 * they await the real work instead of racing ahead as though it had finished.
 * Their arguments are ignored — these are idempotent button handlers where the
 * second click carries the same intent as the first.
 */
export const useInFlightOnce = <TArgs extends unknown[], TResult>(
  action: (...args: TArgs) => Promise<TResult>,
): ((...args: TArgs) => Promise<TResult>) => {
  const inFlightRef = useRef<Promise<TResult> | null>(null);

  // `action` closes over fresh state each render; read it through a ref so the
  // returned callback stays stable without going stale.
  const actionRef = useRef(action);
  actionRef.current = action;

  return useCallback((...args: TArgs) => {
    inFlightRef.current ??= actionRef.current(...args).finally(() => {
      inFlightRef.current = null;
    });
    return inFlightRef.current;
  }, []);
};
